import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import {
    signIn, signOut, fetchAuthSession, getCurrentUser,
    fetchUserAttributes, updateUserAttributes, updatePassword,
    resetPassword, confirmResetPassword, confirmSignIn,
    sendUserAttributeVerificationCode, confirmUserAttribute,
    setUpTOTP, verifyTOTPSetup, updateMFAPreference, fetchMFAPreference,
    type AuthSession, type FetchUserAttributesOutput, type SetUpTOTPOutput,
} from 'aws-amplify/auth';
import { Observable } from 'rxjs';
import { map, shareReplay } from 'rxjs/operators';

import { User, UserAdapter } from '../models/user.model';
import { environment } from '../../../environments/environment';
import Bugsnag from '@bugsnag/js';

export enum LoginResult {
    LOGINSUCCESS,
    LOGINFAILURE,
    INVALIDCREDENTIALS,
    CODEMISMATCH,
    RESETPASSWORD,
    SETNEWPASSWORD,
    MFACHALLENEGE
}

@Injectable({
    providedIn: 'root'
})
export class LoginService {
    private userApi: string;
    private users: Observable<User[]> | null;

    constructor(private http: HttpClient, private userAdapter: UserAdapter, private router: Router) {
        this.userApi = environment.userAdminApiRoot;
        this.users = null;
    }

    private _wrapLoginObservable(promise: Promise<any>): Observable<LoginResult> {
        return new Observable(observer => {
            promise.then((result: any) => {
                // v6 signIn returns { isSignedIn, nextStep: { signInStep } }
                if (result && result.nextStep) {
                    const step: string = result.nextStep.signInStep;
                    if (step === 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED') {
                        observer.next(LoginResult.SETNEWPASSWORD);
                    } else if (step === 'CONFIRM_SIGN_IN_WITH_TOTP_CODE') {
                        observer.next(LoginResult.MFACHALLENEGE);
                    } else if (
                        step === 'CONFIRM_SIGN_IN_WITH_SMS_MFA_CODE' ||
                        step === 'CONFIRM_SIGN_IN_WITH_EMAIL_CODE'
                    ) {
                        console.error('[LoginService] Unsupported MFA type:', step);
                        observer.next(LoginResult.LOGINFAILURE);
                    } else if (step === 'RESET_PASSWORD') {
                        observer.next(LoginResult.RESETPASSWORD);
                    } else {
                        // 'DONE' or any other completed step
                        observer.next(LoginResult.LOGINSUCCESS);
                        this._identifyBugsnagUser();
                    }
                } else {
                    // confirmSignIn, confirmResetPassword etc. return void / minimal objects
                    observer.next(LoginResult.LOGINSUCCESS);
                    this._identifyBugsnagUser();
                }
            })
            .catch((err: any) => {
                // v6 uses err.name instead of err.code
                const code: string = err?.name || '';
                if (code === 'PasswordResetRequiredException') {
                    observer.next(LoginResult.RESETPASSWORD);
                } else if (code === 'NotAuthorizedException') {
                    observer.next(LoginResult.INVALIDCREDENTIALS);
                } else if (code === 'CodeMismatchException') {
                    observer.next(LoginResult.CODEMISMATCH);
                } else {
                    console.error('[LoginService] Unhandled auth error:', code, err?.message);
                    observer.next(LoginResult.LOGINFAILURE);
                }
            })
            .finally(() => observer.complete());
        });
    }

    private _wrapBooleanObservable(promise: Promise<any>, verifyMethod: (result: any) => boolean): Observable<boolean> {
        return new Observable<boolean>(observer => {
            promise
                .then((result: any) => observer.next(verifyMethod(result)))
                .catch(() => observer.next(false))
                .finally(() => observer.complete());
        });
    }

    private _identifyBugsnagUser(): void {
        fetchAuthSession().then((session: AuthSession) => {
            const payload = session.tokens?.idToken?.payload;
            if (payload) {
                const sub = payload['sub'] as string ?? null;
                const email = payload['email'] as string ?? null;
                const name = payload['name'] as string ?? null;
                Bugsnag.setUser(sub, email, name);
            }
        }).catch(() => {});
    }

    public authenticate(username: string, password: string): Observable<LoginResult> {
        return this._wrapLoginObservable(signIn({ username, password }));
    }

    // Continues the in-progress TOTP sign-in challenge.
    public confirmMfa(code: string): Observable<LoginResult> {
        return this._wrapLoginObservable(confirmSignIn({ challengeResponse: code }));
    }

    public isAuthenticated(): Observable<LoginResult> {
        return new Observable(observer => {
            fetchAuthSession().then((session: AuthSession) => {
                if (session.tokens) {
                    observer.next(LoginResult.LOGINSUCCESS);
                } else {
                    observer.next(LoginResult.LOGINFAILURE);
                }
            })
            .catch(() => observer.next(LoginResult.LOGINFAILURE))
            .finally(() => observer.complete());
        });
    }

    public isAuthenticatedAdmin(): Observable<boolean> {
        return new Observable<boolean>(observer => {
            fetchAuthSession().then((session: AuthSession) => {
                const payload = session.tokens?.idToken?.payload;
                if (payload && 'cognito:groups' in payload) {
                    const groups = payload['cognito:groups'] as string[];
                    observer.next(groups.includes('Administrators'));
                } else {
                    observer.next(false);
                }
            })
            .catch(() => observer.next(false))
            .finally(() => observer.complete());
        });
    }

    // Called after signIn returns SETNEWPASSWORD — continues the in-progress sign-in session.
    public confirmNewPassword(newPassword: string): Observable<LoginResult> {
        return this._wrapLoginObservable(confirmSignIn({ challengeResponse: newPassword }));
    }

    public initiateForgotPassword(username: string): Observable<LoginResult> {
        return this._wrapLoginObservable(resetPassword({ username }));
    }

    public resetPassword(username: string, verifyCode: string, newPassword: string): Observable<LoginResult> {
        return this._wrapLoginObservable(
            confirmResetPassword({ username, confirmationCode: verifyCode, newPassword })
        );
    }

    public changePassword(oldPassword: string, newPassword: string): Observable<LoginResult> {
        return this._wrapLoginObservable(updatePassword({ oldPassword, newPassword }));
    }

    // --- TOTP / MFA management ---

    public getMfaPreference(): Observable<'TOTP' | 'NOMFA'> {
        return new Observable(obs => {
            fetchMFAPreference()
                .then(pref => {
                    obs.next(pref.preferred === 'TOTP' ? 'TOTP' : 'NOMFA');
                })
                .catch(() => obs.next('NOMFA'))
                .finally(() => obs.complete());
        });
    }

    public setupTotp(): Observable<SetUpTOTPOutput | null> {
        return new Observable(obs => {
            setUpTOTP()
                .then(details => obs.next(details))
                .catch(() => obs.next(null))
                .finally(() => obs.complete());
        });
    }

    public verifyTotpSetup(code: string): Observable<boolean> {
        return this._wrapBooleanObservable(
            verifyTOTPSetup({ code }).then(() =>
                updateMFAPreference({ totp: 'PREFERRED' })
            ),
            () => true
        );
    }

    public disableTotp(): Observable<boolean> {
        return this._wrapBooleanObservable(
            updateMFAPreference({ totp: 'DISABLED' }),
            () => true
        );
    }

    // --- Token access ---

    public getAPIToken(): Observable<string|null> {
        return new Observable<string|null>(obs => {
            fetchAuthSession()
                .then((session: AuthSession) => obs.next(session.tokens?.idToken?.toString() ?? null))
                .catch(() => obs.next(null))
                .finally(() => obs.complete());
        });
    }

    public getSessionTokens(): Observable<{ idToken: string; accessToken: string; refreshToken: string } | null> {
        return new Observable(obs => {
            fetchAuthSession()
                .then((session: AuthSession) => {
                    const idToken = session.tokens?.idToken?.toString();
                    const accessToken = session.tokens?.accessToken?.toString();
                    // refreshToken is available on the underlying CognitoAuthTokens type
                    const refreshToken = (session as any).tokens?.refreshToken?.toString() ?? '';
                    if (idToken && accessToken) {
                        obs.next({ idToken, accessToken, refreshToken });
                    } else {
                        obs.next(null);
                    }
                })
                .catch(() => obs.next(null))
                .finally(() => obs.complete());
        });
    }

    // --- User attribute management ---

    public getUserAttributes(): Observable<User|null> {
        return new Observable<User|null>(obs => {
            Promise.all([fetchUserAttributes(), getCurrentUser()])
                .then(([attrs, currentUser]) => obs.next(this.userAdapter.adapt({ username: currentUser.username, attributes: attrs })))
                .catch(() => obs.next(null))
                .finally(() => obs.complete());
        });
    }

    public updateUserAttributes(updateAttrs: Record<string, string>): Observable<boolean> {
        return this._wrapBooleanObservable(
            updateUserAttributes({ userAttributes: updateAttrs }),
            () => true
        );
    }

    public resendAttributeVerification(attribute: string): Observable<boolean> {
        return this._wrapBooleanObservable(
            sendUserAttributeVerificationCode({ userAttributeKey: attribute as any }),
            () => true
        );
    }

    public verifyAttribute(attribute: string, verifyCode: string): Observable<boolean> {
        return this._wrapBooleanObservable(
            confirmUserAttribute({ userAttributeKey: attribute as any, confirmationCode: verifyCode }),
            () => true
        );
    }

    public logout(): void {
        signOut().then(() => {
            this.router.navigate(['/login']);
        });
    }

    public getAllUsers(): Observable<User[]> {
        if (!this.users) {
            this.users = this.http.get<any>(`${this.userApi}/listUserNames`).pipe(
                map(response => response.Users.map((item: any) => this.userAdapter.adapt(item))),
                shareReplay(1)
            );
        }
        return this.users;
    }

    public clearCachedUsers(): void {
        this.users = null;
    }
}
